import * as PIXI from 'pixi.js';

export interface TutorialStep {
  text: string;
  position: { x: number; y: number };
  trigger: string;
}

export class Tutorial {
  private container: PIXI.Container;
  private text: PIXI.Text;
  private arrow: PIXI.Text;
  private currentStep = 0;
  private steps: TutorialStep[] = [];
  private isActive = false;
  private dismissCallback: (() => void) | null = null;

  constructor() {
    this.container = new PIXI.Container();
    this.container.eventMode = 'static';

    // Background
    const bg = new PIXI.Graphics();
    bg.roundRect(-160, -40, 320, 80, 12);
    bg.fill({ color: 0x000000, alpha: 0.8 });
    this.container.addChild(bg);

    // Arrow
    this.arrow = new PIXI.Text({
      text: '▲',
      style: { fill: 0xffd700, fontSize: 20 },
    });
    this.arrow.anchor.set(0.5);
    this.arrow.y = -50;
    this.container.addChild(this.arrow);

    // Text
    this.text = new PIXI.Text({
      text: '',
      style: { fill: 0xffffff, fontSize: 18, fontFamily: 'Arial', align: 'center' },
    });
    this.text.anchor.set(0.5);
    this.container.addChild(this.text);

    this.container.on('pointerdown', () => {
      this.dismiss();
    });
  }

  getContainer(): PIXI.Container {
    return this.container;
  }

  setSteps(steps: TutorialStep[]): void {
    this.steps = steps;
    this.currentStep = 0;
  }

  show(stepIndex: number, worldX: number, worldY: number, onDismiss?: () => void): void {
    const step = this.steps[stepIndex];
    if (!step) {
      this.hide();
      return;
    }

    this.currentStep = stepIndex;
    this.isActive = true;
    this.dismissCallback = onDismiss ?? null;

    this.text.text = step.text;
    this.container.x = worldX;
    this.container.y = worldY - 60;
    this.container.visible = true;

    // Animation
    this.container.alpha = 0;
    const fadeIn = () => {
      if (this.container.alpha < 1) {
        this.container.alpha += 0.1;
        requestAnimationFrame(fadeIn);
      }
    };
    fadeIn();
  }

  dismiss(): void {
    if (!this.isActive) return;

    this.isActive = false;
    this.container.visible = false;
    this.currentStep++;

    if (this.dismissCallback) {
      this.dismissCallback();
      this.dismissCallback = null;
    }
  }

  hide(): void {
    this.isActive = false;
    this.container.visible = false;
  }

  getIsVisible(): boolean {
    return this.isActive;
  }

  getCurrentStep(): number {
    return this.currentStep;
  }

  hasMoreSteps(): boolean {
    return this.currentStep < this.steps.length;
  }
}
